import { Trans, useTranslation } from 'react-i18next';
import NewAuthLayout from '../NewLogin/layout/Layout';
import styles from './TermsAndCondition.module.scss';
import { useMutation } from '@apollo/client';
import { VALIDATE_USER } from 'Services/Api/Users/Mutations';
import { useHistory } from 'react-router-dom';
import { useStores } from 'Hooks/useStore';
import { getAppRoutes } from 'Pages/App/App.constants';
import { PhoneNumber } from '../Auth.types';
import { SetShouldMoveToTermsAndConditions } from '../Login/Steps/ChangePasswordForm';
import { AuthUser } from 'Services/Api/Auth/Types';
import { Form, Formik, FormikHelpers } from 'formik';
import { Button, Checkbox, Input, Notification } from 'Components';
import { checkApolloError } from 'Helpers/graphql';
import { APIErrorCodes } from 'app-types';
import { termsAndConditionsSchema } from '../Login/schema';
import { components } from '../Login/Steps/AcceptTermsAndConditions.components';

type Values = PhoneNumber;

interface AcceptTermsAndConditionsProps
	extends SetShouldMoveToTermsAndConditions {
	setCognitoUser(user: AuthUser | null): void;
}

const TermsAndCondition: React.FC<AcceptTermsAndConditionsProps> = ({
	setShouldMoveToTermsAndConditions,
	setCognitoUser
}) => {
	const { t } = useTranslation();
	const [validateUser] = useMutation(VALIDATE_USER);
	const history = useHistory();
	const { authStore } = useStores();

	const initialValues = {};

	const onSubmit = async (values: Values, helpers: FormikHelpers<Values>) => {
		helpers.setStatus('');
		const appRoutes = getAppRoutes();
		try {
			await authStore.checkAuth(true);
			const { phoneNumber } = values;
			await validateUser({
				variables: {
					input: {
						phoneNumber: phoneNumber || null
					}
				}
			});
			if (!authStore.isTwoFactorAuthorizationEnabled) {
				Notification.success({
					description: t('auth.changePassword.notificationSuccess')
				});
			}

			history.push(appRoutes.HOME, { firstTimeSignIn: true });
		} catch (error) {
			helpers.setSubmitting(false);
			const apolloError = checkApolloError(error);

			if (apolloError.is(APIErrorCodes.ValidationIsCognitoPhoneNumber)) {
				helpers.setErrors({
					phoneNumber: t(
						`user.errorCodes.${APIErrorCodes.ValidationIsCognitoPhoneNumber}`
					)
				});
				return;
			}
			setShouldMoveToTermsAndConditions(false);
			setCognitoUser(null);
		}
	};

	return (
		<NewAuthLayout variant="centered">
			<div className={styles.root}>
				<h1>Account Setup & Agreement</h1>
				<p>Please complete the following steps to continue.</p>
				<div className={styles.securityVerification}>
					<h3>Security Verification (Optional)</h3>

					<p>
						Add a phone number to enhance your account security.
						<br />
						Your phone number may be used for:
					</p>

					<ul>
						<li>SMS notifications</li>
						<li>Multi-Factor Authentication (MFA)</li>
						<li>Account recovery</li>
					</ul>
				</div>
				<Formik
					initialValues={initialValues}
					onSubmit={onSubmit}
					validationSchema={termsAndConditionsSchema}
				>
					{({ isValid, isSubmitting, dirty }) => (
						<Form className={styles.form}>
							<Input.FormikField
								name="phoneNumber"
								label={
									t('auth.login.inputs.phoneNumber') +
									` (optional)`
								}
								className={styles.inputPhoneStyles}
							/>

							<h4 className={styles.firstLoginTitle}>
								{t('auth.login.consent.title')}
							</h4>
							<p>{t('auth.login.consent.text')}</p>

							<Checkbox.FormikField name="termsOfUse" help={null}>
								<Trans
									i18nKey={'auth.login.inputs.termsOfUse'}
									components={components}
								/>
								{`*`}
							</Checkbox.FormikField>
							<Checkbox.FormikField
								name="privacyPolicy"
								help={null}
							>
								<Trans
									i18nKey={'auth.login.inputs.privacyPolicy'}
									components={components}
								/>
								{`*`}
							</Checkbox.FormikField>
							<Button
								htmlType="submit"
								type="primary"
								shape="round"
								disabled={!(isValid && dirty) || isSubmitting}
								loading={isSubmitting}
								fullWidth
							>
								{t('auth.login.termsAndConditions.submitText')}
							</Button>
						</Form>
					)}
				</Formik>
			</div>
		</NewAuthLayout>
	);
};

export default TermsAndCondition;
